import { useEffect, useState, type ReactNode } from "react"
import { useChat } from "@/features/chat"
import { getParamsFromStorage } from "@/shared/lib/storage-params"
import { PalmistryContext } from "./palmistry-context"

const TEN_MINUTES = 10 * 60 * 1000

/**
 * Tracks the 10-minute "palm report in progress" lock that begins when a hand
 * photo is captured. Ported from lpr-web's PalmistryTimerProvider.
 */
export function PalmistryTimerProvider({ children }: { children: ReactNode }) {
  const [timeLeft, setTimeLeft] = useState<string | null>(null)
  const { setIsPalmReadingLocked, setPalmReportModalOpen } = useChat()

  useEffect(() => {
    // Poll every second so the lock + countdown stay live even when the hand
    // photo is captured after this provider mounted (i.e. mid-session, no reload).
    const update = () => {
      const { hand_photo_palmistry } = getParamsFromStorage(["hand_photo_palmistry"])
      if (!hand_photo_palmistry) {
        setIsPalmReadingLocked(false)
        setTimeLeft(null)
        return
      }

      const remaining = TEN_MINUTES - (Date.now() - new Date(hand_photo_palmistry).getTime())
      if (remaining <= 0) {
        setIsPalmReadingLocked(false)
        setPalmReportModalOpen(false)
        setTimeLeft(null)
        return
      }

      setIsPalmReadingLocked(true)
      const minutes = Math.floor(remaining / 1000 / 60)
      const seconds = Math.floor((remaining / 1000) % 60)
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, "0")}`)
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [setIsPalmReadingLocked, setPalmReportModalOpen])

  return <PalmistryContext.Provider value={{ timeLeft }}>{children}</PalmistryContext.Provider>
}
